/**
 * Sentry's screen recording of an error. A visitor who has not allowed
 * analytics is never recorded, so `client.tsx` leaves the integration out and
 * the consent loader adds it with `startErrorReplay` once analytics is
 * allowed. Errors and traces need no consent and stay in `Sentry.init`.
 *
 * Aliases: session replay, Sentry replay, error recording
 */

import * as Sentry from "@sentry/tanstackstart-react";

import { SENTRY_REPLAY_PRIVACY } from "~/lib/sentry-privacy";
import { isReplayWorthyError } from "~/lib/sentry-replay-filter";

/**
 * Starts buffering the page; the SDK keeps a replay only when an error
 * follows. Nothing starts while Sentry is off (local dev, E2E): `Sentry.init`
 * sets up no integration then, and `addIntegration` would record the page
 * for nothing.
 */
export function startErrorReplay(): void {
  if (!Sentry.isEnabled()) return;
  Sentry.addIntegration(
    Sentry.replayIntegration({
      // Replays show layout and clicks only: decrypted notes, goals and chats
      // must never reach Sentry as text or media.
      ...SENTRY_REPLAY_PRIVACY,
      // Buffered replays flush on the `beforeSendEvent` hook, which runs before
      // `beforeSend` in `client.tsx` — so auth-session / blocked-analytics
      // errors we drop there still burn one of our 50 monthly replays
      // (yielding replays with zero linked errors). This is the only veto that
      // runs at flush time; keep it in sync with the `beforeSend` drops. See
      // DCM-M4 / replay budget.
      beforeErrorSampling: isReplayWorthyError,
    }),
  );
}
